import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLocale } from '@wikigold/i18n';
import { api, copyText, mutation } from '../lib';
import { activityNow, deadlineLabel, evidenceAccess, historyDayState, profileIsComplete, requiresProfile, selectFlowRun } from './participation-flow';
import { GoldGame, type GoldRunView } from './GoldGame';
import { useGameViewport } from '../components/use-game-viewport';
import { preloadGameAssets } from '../components/sprite-assets';
import {
  APP_ENTRY_URL,
  CALENDAR_GUIDE_STEP,
  GUIDE_STEPS,
} from './content';
import '../style.css';
import '../public-flow.css';
import '../game.css';
import '../gold-v2.css';
import '../gold-v2-final.css';
import { LanguagePicker } from '../i18n/LanguagePicker';
import { stripLocale } from '../i18n/locale-routing';
import { gameRouteHref } from './game-route';
import { i18n as appI18n } from '../i18n/I18nProvider';
import { introCopy } from '../intro/content';

const DemoControls = lazy(() => import('./DemoControls'));

export interface GoldConfig {
  rules: {
    version: string;
    activity_days: 7;
    game: { seconds: 45; ticks: 1350; gold_unit: 'g'; gold_values: Record<string, number> };
    main_prize_ladder_cents: { first: number; last: number; cents: number }[];
    schedule: { provisional_board_earliest_day: number; final_board_earliest_day: number };
  };
  current: { dateKey: string; dayNumber: number; dayEndMs: number; remainingDays: number };
  campaign: { startDate: string; timezone: string };
  now: string;
  observedAt: number;
  demoControls: boolean;
  frozen?: boolean;
  integrations: {
    iosDownloadUrl: string | null;
    androidDownloadUrl: string | null;
    officialDownloadUrl: string | null;
    appOpenUrl: string | null;
    appScreenshots: string[] | null;
    status: 'configured' | 'not_configured';
    appendQueryParameters: boolean;
    experienceRecords: {
      allowed: boolean;
      requiredNote: string;
      purchaseOrDepositRequired: boolean;
    };
    calendarVisibleFields: string[];
  };
  schedule: { activity: string; submissionCutoff: string; provisionalEarliest: string; finalEarliest: string; payout: string };
  playBeforeBinding: boolean;
  mapMode: string;
  testMock: boolean;
}

interface Person {
  id: string;
  uid: string | null;
  nickname: string;
  email: string | null;
  identityStatus: string;
  version: number;
  registrationSequence: number | null;
  profileComplete: boolean;
  profileUpdatedAt: string | null;
}

interface Submission {
  id: string;
  runId: string;
  dateKey: string;
  submissionVersion: number;
  files: string[];
  status: string;
  primaryMaterial?: string;
  reason: string;
  firstSubmittedAt: string;
  deadlineAt: string;
  supplementDeadline?: string | null;
  version: number;
}

interface GoldMe {
  participant: Person;
  runs: GoldRunView[];
  currentRun: GoldRunView | null;
  validDays: { dateKey: string; goldAmount: number; reviewedAt: string }[];
  totalGold: number;
  submissions: Submission[];
  evidenceStatus: string;
  testMock: boolean;
}

interface Board {
  status: string;
  title: string;
  rows: {
    rank: number;
    tieStart: number;
    tieEnd: number;
    nickname: string;
    totalGold: number;
    validDays: number;
    prizeCents: number;
    mine?: boolean;
  }[];
}

type GoldPage = 'home' | 'play' | 'result' | 'record' | 'evidence' | 'board' | 'guide' | 'recover';
type ProfileMode = 'claim' | 'edit';
const paths: Record<GoldPage, string> = {
  home: '/',
  play: '/play',
  result: '/result',
  record: '/progress',
  evidence: '/evidence',
  board: '/leaderboard',
  guide: '/guide',
  recover: '/recover',
};

const statusCopy: Record<string, string> = {
  submitted: 'status.submitted',
  pending_second: 'status.pendingSecond',
  supplement_requested: 'status.supplement',
  approved: 'status.approved',
  rejected: 'status.rejected',
  superseded: 'status.superseded',
};

function pageForPath(path: string): GoldPage {
  const plainPath = stripLocale(path);
  return (Object.entries(paths).find(([, value]) => value === plainPath)?.[0] as GoldPage | undefined) ?? 'home';
}

function statusMessage(status: string, t: Translate): string {
  return t(statusCopy[status] ?? 'status.submitted');
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function messageFor(error: unknown, t: Translate): string {
  const code = error instanceof Error ? error.message : String(error);
  const known: Record<string, string> = {
    insufficient_time_for_full_run: 'error.insufficientTime',
    not_current_activity_day: 'error.notCurrentDay',
    submission_deadline_passed: 'error.deadline',
    settled_run_required: 'error.runRequired',
    gold_run_not_settled: 'error.runRequired',
    identity_already_bound: 'error.identityBound',
    uid_already_bound: 'recovery.body',
    incomplete_prize_profile: 'profile.required',
    gold_run_scope: 'error.runScope',
    gold_activity_scope: 'error.runScope',
    registration_required: 'error.registration',
    gold_unresolved_submissions: 'error.unresolved',
    file_too_large: 'error.fileSize',
    file_type_mismatch: 'error.fileType',
    gold_session_expired: 'error.session',
    invalid_recovery: 'error.invalidRecovery',
    gold_replay_mismatch: 'error.replay',
    clipboard_unavailable: 'error.clipboard',
    invitation_removed: 'error.invitation',
  };
  return t(known[code] ?? (/401|session/.test(code) ? 'error.session' : 'error.generic'));
}

function isActiveDay(day?: string): boolean {
  return Boolean(day && /^D[1-7]$/.test(day));
}

function instantLabel(value: number | string, timezone = 'Asia/Shanghai'): string {
  const locale = normalizeLocale(appI18n.resolvedLanguage ?? appI18n.language);
  return deadlineLabel(value, locale, timezone);
}

function latestByRun(me: GoldMe): Map<string, Submission> {
  const latest = new Map<string, Submission>();
  for (const submission of me.submissions) {
    const current = latest.get(submission.runId);
    if (!current || submission.submissionVersion > current.submissionVersion) latest.set(submission.runId, submission);
  }
  return latest;
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('file_read_failed'));
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ''));
    reader.readAsDataURL(file);
  });
}

async function loadConfig(): Promise<GoldConfig> {
  const config = await api<GoldConfig>('/api/gold-v2/config');
  return { ...config, observedAt: performance.now() };
}

export default function GoldApp(): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  const [page, setPage] = useState<GoldPage>(() => pageForPath(location.pathname));
  const [config, setConfig] = useState<GoldConfig | null>(null);
  const [me, setMe] = useState<GoldMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [savePrompt, setSavePrompt] = useState(false);
  const [profileMode, setProfileMode] = useState<ProfileMode | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => new URLSearchParams(location.search).get('run'));
  const [, setMonotonicNow] = useState(() => performance.now());

  const go = useCallback((next: GoldPage, runId?: string) => {
    const selected = ['play', 'result', 'evidence', 'guide'].includes(next)
      ? runId ?? new URLSearchParams(location.search).get('run') : null;
    history.pushState({}, '', gameRouteHref(locale, paths[next], location, selected));
    setSelectedRunId(selected);
    setPage(next);
    setError('');
    window.scrollTo({ top: 0 });
  }, [locale]);

  const refresh = useCallback(async () => {
    const [nextConfig, nextMe] = await Promise.all([
      loadConfig(),
      api<GoldMe>('/api/gold-v2/me'),
    ]);
    setConfig(nextConfig);
    setMe(nextMe);
    return nextMe;
  }, []);

  const act = useCallback(async (work: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (cause) {
      setError(messageFor(cause, t));
    } finally {
      setBusy(false);
    }
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const nextConfig = await loadConfig();
      await api('/api/gold-v2/session', {});
      const nextMe = await api<GoldMe>('/api/gold-v2/me');
      await preloadGameAssets(nextConfig.current.dateKey);
      if (!active) return;
      setConfig(nextConfig);
      setMe(nextMe);
    })().catch(cause => {
      if (active) setError(messageFor(cause, t));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [t]);

  useEffect(() => {
    const pop = () => {
      setPage(pageForPath(location.pathname));
      setSelectedRunId(new URLSearchParams(location.search).get('run'));
      setProfileMode(null);
    };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 3500);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const currentRun = me?.currentRun ?? null;
  const latest = me ? latestByRun(me) : new Map<string, Submission>();
  const currentSubmission = currentRun ? latest.get(currentRun.id) : undefined;
  const currentValid = me?.validDays.find(day => day.dateKey === config?.current.dateKey);
  const bound = Boolean(me && profileIsComplete(me.participant));
  const now = config ? activityNow(config, performance.now()) : NaN;
  const flowRun = me ? selectFlowRun(me.runs, selectedRunId, currentRun, page) : null;
  const flowSubmission = flowRun ? latest.get(flowRun.id) : undefined;
  const flowValid = me?.validDays.find(day => day.dateKey === flowRun?.dateKey);
  const profileRequired = Boolean(me && requiresProfile(page, me.participant));
  useGameViewport(page === 'play');

  useEffect(() => {
    // GoldGame owns live snapshots and terminal recovery. A global refresh must
    // not replace its selected run during a failed settlement refresh.
    if (!config || page === 'play') return;
    const tick = window.setInterval(() => setMonotonicNow(performance.now()), 500);
    const synchronize = () => {
      setMonotonicNow(performance.now());
      void refresh().catch(() => {});
    };
    const visible = () => { if (document.visibilityState === 'visible') synchronize(); };
    const poll = window.setInterval(synchronize, 30_000);
    const boundary = !config.frozen && config.current.dayEndMs > Date.parse(config.now)
      ? window.setTimeout(synchronize, Math.max(1, config.current.dayEndMs - activityNow(config, performance.now()) + 20)) : undefined;
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.clearInterval(tick);
      window.clearInterval(poll);
      window.clearTimeout(boundary);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [config, refresh, page]);

  useEffect(() => {
    if (!flowRun || selectedRunId || !['play', 'result', 'evidence', 'guide'].includes(page)) return;
    history.replaceState({}, '', gameRouteHref(locale, paths[page], location, flowRun.id));
    setSelectedRunId(flowRun.id);
  }, [flowRun, selectedRunId, page, locale]);

  const startGame = () => void act(async () => {
    await preloadGameAssets(config!.current.dateKey);
    const next = await api<GoldRunView>('/api/gold-v2/runs', {
      dateKey: config!.current.dateKey,
      ...mutation(0),
    });
    if (next.phase === 'RUNNING') go('play', next.id);
    else go('result', next.id);
    await refresh();
  });

  const copyRecovery = () => void act(async () => {
    const result = await api<{ recoveryPath: string }>('/api/gold-v2/recovery', mutation(0));
    await copyText(new URL(result.recoveryPath, location.origin).href);
    setNotice(t('recovery.copied'));
  });

  if (loading) {
    return <Shell page={page} go={go}><div className="gold-loading"><span>⛏</span><p>{t('common.loading')}</p></div></Shell>;
  }
  if (!config || !me) {
    return <Shell page={page} go={go}><div className="gold-notice danger">{error || t('error.load')}</div></Shell>;
  }

  return <Shell page={page} go={go} mock={config.testMock} bound={bound} onRecovery={copyRecovery}>
    {error && <div className="gold-notice danger" role="alert">{error}<button onClick={() => void act(async () => { await refresh(); })}>{t('common.retry')}</button></div>}
    {notice && <div className="gold-toast" role="status">{notice}</div>}
    {config.testMock && config.demoControls && page !== 'play' && <Suspense fallback={null}><DemoControls config={config} refresh={refresh} goHome={() => go('home')} setNotice={setNotice} onError={cause => setError(messageFor(cause, t))}/></Suspense>}

    {page === 'home' && <Home config={config} now={now} me={me} currentRun={currentRun} currentSubmission={currentSubmission} currentValid={currentValid} bound={bound} busy={busy} start={startGame} go={go} save={() => setSavePrompt(true)} rules={() => setRulesOpen(true)}/>}
    {page === 'play' && <PlayPage config={config} currentRun={flowRun} busy={busy} start={startGame} go={go} refresh={refresh} onError={setError}/>}
    {page === 'result' && <ResultPage config={config} now={now} run={flowRun} submission={flowSubmission} valid={flowValid} bound={bound} go={go} save={() => setSavePrompt(true)} onClaim={() => setProfileMode('claim')}/>}
    {page === 'record' && <RecordPage config={config} now={now} me={me} go={go} onEditProfile={() => setProfileMode('edit')}/>}
    {page === 'evidence' && !profileRequired && <EvidencePage key={flowRun?.id ?? 'none'} config={config} now={now} selected={flowRun} me={me} act={act} refresh={refresh} go={go} setNotice={setNotice}/>}
    {page === 'board' && <BoardPage me={me} rules={() => setRulesOpen(true)}/>}
    {page === 'guide' && !profileRequired && <GuidePage config={config} go={go}/>}
    {page === 'recover' && <RecoveryPage act={act} refresh={refresh} go={go} setNotice={setNotice}/>}

    {profileRequired && <section className="gold-card gold-single-action"><h1>{t('profile.title')}</h1><p>{t(flowRun?.phase === 'SETTLED' ? 'profile.required' : 'error.runRequired')}</p><button className="gold-secondary" onClick={() => go(flowRun ? 'result' : 'home', flowRun?.id)}>{t('common.back')}</button></section>}
    {profileRequired && flowRun?.phase === 'SETTLED' && <PrizeProfileDialog key={flowRun.id} mode="claim" participant={me.participant} run={flowRun} config={config} act={act} refresh={refresh} error={error} onRecover={() => go('recover')} onSaved={() => {}} onClose={() => go('result', flowRun.id)}/>}
    {savePrompt && <SavePrompt onClose={() => setSavePrompt(false)} onRecovery={copyRecovery}/>}
    {profileMode && <PrizeProfileDialog mode={profileMode} participant={me.participant} run={flowRun} config={config} act={act} refresh={refresh} error={error} onRecover={() => { setProfileMode(null); go('recover'); }} onClose={() => setProfileMode(null)}/>}
    {rulesOpen && <RulesDialog config={config} onClose={() => setRulesOpen(false)}/>}
  </Shell>;
}

function Shell({ children, page, go, mock, bound, onRecovery }: {
  children: React.ReactNode;
  page: GoldPage;
  go: (page: GoldPage, runId?: string) => void;
  mock?: boolean;
  bound?: boolean;
  onRecovery?: () => void;
}) {
  const { t } = useTranslation();
  return <div className={`gold-app gold-final-app ${page === 'play' ? 'playing' : ''}`}>
    <header className="gold-header">
      <button className="gold-brand" onClick={() => go('home')}><span>✦</span><b>WikiGold</b><small>{t('app.subtitle')}</small></button>
      <div className="gold-header-actions"><LanguagePicker />{mock && <span className="gold-mock">{t('app.mock')}</span>}{bound && onRecovery && <button onClick={onRecovery} aria-label={t('recovery.copy')}>{t('app.recover')}</button>}</div>
    </header>
    <main className="gold-main">{children}</main>
    <nav className="gold-nav gold-nav-final" aria-label={t('nav.main')}>
      {([
        ['home', '⌂', t('nav.home')],
        ['record', '▤', t('nav.record')],
        ['board', '♛', t('nav.board')],
      ]).map(([target, icon, label]) => <button key={target} className={page === target ? 'active' : ''} onClick={() => go(target as GoldPage)}><span>{icon}</span><small>{label}</small></button>)}
    </nav>
  </div>;
}

type AppEntryPlacement = 'home_download' | 'home_go_app' | 'result_go_app' | 'guide_download' | 'guide_go_app';

function AppEntryLink({ children, placement, className = 'gold-primary' }: {
  children: React.ReactNode;
  placement: AppEntryPlacement;
  className?: string;
}) {
  const track = () => {
    void fetch('/api/gold-v2/app-entry-click', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ placement }),
      keepalive: true,
    }).catch(() => {});
  };
  return <a className={`${className} gold-app-link`} data-testid="app-entry-link" href={APP_ENTRY_URL} onClick={track}>{children}</a>;
}

function Home({ config, now, me, currentRun, currentSubmission, currentValid, bound, busy, start, go, save, rules }: {
  config: GoldConfig;
  now: number;
  me: GoldMe;
  currentRun: GoldRunView | null;
  currentSubmission?: Submission;
  currentValid?: { dateKey: string; goldAmount: number };
  bound: boolean;
  busy: boolean;
  start: () => void;
  go: (page: GoldPage, runId?: string) => void;
  save: () => void;
  rules: () => void;
}) {
  const { t, i18n } = useTranslation();
  const active = isActiveDay(config.current.dateKey);
  const played = me.runs.length;
  const access = evidenceAccess(currentRun, currentSubmission, now);
  const expired = access.state === 'initial_expired' || access.state === 'supplement_expired';
  const activityLabel = active
    ? t('home.dayLabel', { day: config.current.dayNumber })
    : config.current.dateKey === 'before_start' ? t('home.upcoming') : t('home.ended');
  const todayStatus = expired ? t(access.state === 'supplement_expired' ? 'evidence.supplementExpired' : 'evidence.initialExpired') : currentValid
    ? t('home.todayComplete')
    : currentSubmission
      ? statusMessage(currentSubmission.status, t)
      : currentRun?.phase === 'SETTLED'
        ? bound ? t('home.waitApp') : t('home.claimReady')
        : currentRun ? t('home.playing') : active ? t('home.notStarted') : config.current.dateKey === 'before_start' ? t('home.upcoming') : t('home.ended');

  const primary = (() => {
    if (currentRun?.phase === 'RUNNING') return <button className="gold-primary" onClick={() => go('play', currentRun.id)}>{t('action.continue')}</button>;
    if (expired) return <button className="gold-primary" onClick={() => go('record')}>{t('action.viewRecord')}</button>;
    if (currentRun?.phase === 'SETTLED' && !bound) return <button className="gold-primary" onClick={() => go('result')}>{t('action.claim')}</button>;
    if (currentRun?.phase === 'SETTLED' && bound && !currentSubmission) return <AppEntryLink placement="home_go_app">{t('action.goGold')}</AppEntryLink>;
    if (currentSubmission?.status === 'supplement_requested') return <button className="gold-primary" onClick={() => go('evidence')}>{t('action.supplement')}</button>;
    if (currentSubmission?.status === 'rejected') return <button className="gold-primary" onClick={() => go('result')}>{t('action.reason')}</button>;
    if (currentSubmission || currentValid) return <button className="gold-primary" onClick={() => go('record')}>{currentValid || currentSubmission?.status === 'approved' ? t('action.viewComplete') : t('action.viewReview')}</button>;
    if (active) return <button className="gold-primary" disabled={busy} onClick={start}>{busy ? t('action.preparing') : t('action.start')}</button>;
    return <button className="gold-primary" onClick={() => go('board')}>{t('action.viewBoard')}</button>;
  })();

  const useEnglishPoster = (i18n.resolvedLanguage ?? i18n.language).toLowerCase().startsWith('en');
  const totalPrize = introCopy[normalizeLocale(i18n.resolvedLanguage ?? i18n.language)].totalPrize;

  return <>
    <section className={`gold-final-hero${useEnglishPoster ? ' gold-final-hero-poster' : ''}`}>
      {useEnglishPoster ? <img
        className="gold-final-poster"
        src="/assets/gold-prize-pool-en-v2.jpg"
        alt={`${t('home.eyebrow')}. ${totalPrize}. ${t('home.pitch')} ${t('home.how')}`}
      /> : <>
        <div className="gold-final-copy">
          <span>{t('home.eyebrow')}</span>
          <h1><span>{t('home.prizeLead')}</span><strong>US$500</strong></h1>
          <h2>{t('home.pitch')}</h2>
          <p>{t('home.how')}</p>
        </div>
        <div className="gold-final-reward-stack" aria-hidden="true">
          <span className="gold-final-card gold-final-card-back"><b>GIFT</b><i>CARD</i></span>
          <span className="gold-final-card gold-final-card-front"><b>GIFT</b><i>CARD</i></span>
          <span className="gold-final-giftbox"><i /></span>
        </div>
        <img className="gold-final-miner" src="/assets/miner_idle.webp" alt="" aria-hidden="true"/>
        <img className="gold-final-nugget" src="/assets/gold_large.webp" alt="" aria-hidden="true"/>
      </>}
    </section>
    <p className="gold-total-prize">{totalPrize}</p>

    <section className="gold-final-main-card">
      <div className="gold-final-start-copy">
        <b>{activityLabel} · {todayStatus}</b>
        <p>{t('home.condition')}</p>
      </div>
      {primary}
      <div className="gold-final-meta">
        <span>{t('home.played', { count: played })}</span>
        <span>{t('home.listed', { count: me.validDays.length })}</span>
        <button onClick={rules} aria-label={t('board.rulesAria')}>{t('home.rulesLink')}</button>
        <button onClick={save}>{t('home.bookmark')}</button>
      </div>
      {currentRun?.phase === 'SETTLED' && bound && !currentSubmission && access.canUpload && <button className="gold-text" onClick={() => go('evidence')}>{t('home.recentShot')}</button>}
    </section>

    <p className="gold-fineprint">{t('home.footer')}</p>
  </>;
}

function PlayPage({ config, currentRun, busy, start, go, refresh, onError }: {
  config: GoldConfig;
  currentRun: GoldRunView | null;
  busy: boolean;
  start: () => void;
  go: (page: GoldPage, runId?: string) => void;
  refresh: () => Promise<GoldMe>;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  if (currentRun?.phase === 'RUNNING') {
    return <GoldGame initial={currentRun} onError={onError} onSettled={async run => { await refresh(); go('result', run.id); }}/>;
  }
  if (currentRun?.phase === 'SETTLED') {
    return <section className="gold-card gold-single-action"><h1>{t('play.ended')}</h1><p>{t('play.saved')}</p><button className="gold-primary" onClick={() => go('result', currentRun.id)}>{t('play.viewResult')}</button></section>;
  }
  return <section className="gold-card gold-single-action">
    <span className="gold-kicker">{t('play.official')}</span>
    <h1>{t('play.startTitle')}</h1>
    <p>{t('play.ready')}</p>
    <button className="gold-primary" disabled={busy || !isActiveDay(config.current.dateKey)} onClick={start}>{busy ? t('action.preparing') : t('action.start')}</button>
  </section>;
}

function ResultPage({ config, now, run, submission, valid, bound, go, save, onClaim }: {
  config: GoldConfig;
  now: number;
  run: GoldRunView | null;
  submission?: Submission;
  valid?: { dateKey: string; goldAmount: number };
  bound: boolean;
  go: (page: GoldPage, runId?: string) => void;
  save: () => void;
  onClaim: () => void;
}) {
  const { t } = useTranslation();
  if (!run) {
    return <section className="gold-card gold-single-action"><h1>{t('result.noRun')}</h1><button className="gold-primary" onClick={() => go('home')}>{t('common.back')}</button></section>;
  }
  if (run.phase !== 'SETTLED') {
    return <section className="gold-card gold-single-action"><h1>{t('play.stillRunning')}</h1><button className="gold-primary" onClick={() => go('play', run.id)}>{t('play.continue')}</button></section>;
  }
  const finalDay = run.dateKey === 'D7';
  const access = evidenceAccess(run, submission, now);
  return <>
    <section className="gold-result-hero">
      <span>✓</span><small>{t('result.caught')}</small>
      <h1>{t('result.score', { score: run.goldAmount })}</h1>
      <p>{t('home.condition')}</p>
    </section>
    {!bound && <section className="gold-card gold-single-action">
      <button className="gold-primary" data-testid="gold-claim" onClick={onClaim}>{t('action.claim')}</button>
    </section>}
    {!submission && access.state === 'initial_expired' && <div className="gold-notice danger">{t('evidence.initialExpired')}</div>}
    {bound && !submission && access.canUpload && <section className="gold-card gold-single-action">
      <h2>{t('result.goAppTitle')}</h2>
      <AppEntryLink placement="result_go_app">{t('action.goGold')}</AppEntryLink>
      <div className="gold-result-alternatives">
        <button className="gold-primary gold-evidence-shortcut" onClick={() => go('evidence')}><span aria-hidden="true">▣</span>{t('home.recentShot')}</button>
        <button className="gold-secondary gold-guide-shortcut" onClick={() => go('guide')}><span aria-hidden="true">?</span>{t('action.guide')}</button>
      </div>
    </section>}
    {submission && <SubmissionState submission={submission} score={run.goldAmount} finalDay={finalDay} canSupplement={access.canUpload} timezone={config.campaign.timezone} go={go}/>}
    {valid && <button className="gold-secondary" onClick={() => go('board')}>{t('action.viewBoard')}</button>}
    <div className="gold-result-footer">
      <button className="gold-text" onClick={() => go('guide')}>{t('action.viewGuide')}</button>
      <button className="gold-text" onClick={save}>{t('home.bookmark')}</button>
    </div>
  </>;
}

function SubmissionState({ submission, score, finalDay, canSupplement, timezone, go }: { submission: Submission; score: number; finalDay: boolean; canSupplement: boolean; timezone: string; go: (page: GoldPage, runId?: string) => void }) {
  const { t } = useTranslation();
  if (submission.status === 'approved') {
    return <section className="gold-approved-state gold-final-state"><span>✓</span><div><h2>{t('result.approved', { score, ending: finalDay ? t('result.final') : t('result.tomorrow') })}</h2></div></section>;
  }
  if (submission.status === 'supplement_requested') {
    return <section className="gold-card gold-single-action"><h2>{submission.reason}</h2><p>{submission.supplementDeadline ? instantLabel(submission.supplementDeadline, timezone) : ''}</p>{canSupplement ? <button className="gold-primary" onClick={() => go('evidence')}>{t('action.supplement')}</button> : <p>{t('evidence.supplementExpired')}</p>}</section>;
  }
  if (submission.status === 'rejected') {
    return <section className="gold-card gold-single-action"><h2>{submission.reason}</h2><p>{t('result.notRanked')}</p><button className="gold-secondary" onClick={() => go('record')}>{t('action.reason')}</button></section>;
  }
  return <section className="gold-card gold-single-action">
    <h2>{t('result.submitted', { score, ending: finalDay ? t('result.final') : t('result.tomorrow') })}</h2>
    <button className="gold-secondary" onClick={() => go('evidence')}>{t('result.receipt')}</button>
    <button className="gold-primary gold-result-record-link" onClick={() => go('record')}>{t('action.viewRecord')}</button>
  </section>;
}

function PrizeProfileDialog({ mode, participant, run, config, act, refresh, error, onSaved, onRecover, onClose }: {
  mode: ProfileMode;
  participant: Person;
  run: GoldRunView | null;
  config: GoldConfig;
  act: (work: () => Promise<void>) => Promise<void>;
  refresh: () => Promise<GoldMe>;
  error: string;
  onSaved?: () => void;
  onRecover: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [nickname, setNickname] = useState(participant.nickname);
  const [uid, setUid] = useState(participant.uid ?? '');
  const [email, setEmail] = useState(participant.email ?? '');
  const [helpOpen, setHelpOpen] = useState(false);
  const nicknameValid = Array.from(nickname.trim()).length >= 1
    && Array.from(nickname.trim()).length <= 20
    && !/[\u0000-\u001f\u007f-\u009f]/u.test(nickname);
  const uidValid = /^[0-9]{10}$/.test(uid);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && email.trim().length <= 254;
  const canSave = nicknameValid && uidValid && emailValid;
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) return;
    void act(async () => {
      if (!participant.registrationSequence) {
        if (!run) throw new Error('settled_run_required');
        await api('/api/gold-v2/register', {
          uid,
          nickname: nickname.trim(),
          email: email.trim(),
          runId: run.id,
          rulesVersion: config.rules.version,
          ...mutation(participant.version),
        });
      } else {
        await api('/api/gold-v2/profile', {
          nickname: nickname.trim(),
          email: email.trim(),
          ...mutation(participant.version),
        });
      }
      await refresh();
      setHelpOpen(false);
      (onSaved ?? onClose)();
    });
  };
  return <>
    <div className="gold-backdrop" role="dialog" aria-modal="true" aria-labelledby="gold-profile-title">
      <section className="gold-dialog gold-profile-dialog">
        <div className="gold-lightbox-head">
          <div><span className="gold-kicker">{mode === 'edit' ? t('profile.editKicker') : t('profile.firstKicker')}</span><h2 id="gold-profile-title">{t('profile.title')}</h2></div>
          <button type="button" className="gold-lightbox-close" onClick={onClose} aria-label={t('profile.closeAria')}>{t('common.close')}</button>
        </div>
        {error && <p className="gold-notice danger" role="alert">{error}</p>}
        <form className="gold-form" onSubmit={submit}>
          <label>{t('profile.nickname')}<input value={nickname} onChange={event => setNickname(event.target.value)} data-testid="gold-nickname" required maxLength={20} autoComplete="nickname" placeholder={t('profile.nicknamePlaceholder')}/></label>
          <label>{t('profile.uid')}<input value={uid} onChange={event => setUid(event.target.value)} data-testid="gold-uid" required minLength={10} maxLength={10} pattern="[0-9]{10}" inputMode="numeric" placeholder={t('profile.uidPlaceholder')} autoComplete="off" readOnly={Boolean(participant.registrationSequence)}/></label>
          <div className="gold-uid-help-row"><small>{t('profile.uidHint')}</small><button type="button" onClick={() => setHelpOpen(true)}>{t('profile.uidHelp')}</button></div>
          <label>{t('profile.email')}<input value={email} onChange={event => setEmail(event.target.value)} data-testid="gold-prize-email" required type="email" maxLength={254} autoComplete="email" placeholder={t('profile.emailPlaceholder')}/></label>
          <small className="gold-uid-format">{t('profile.emailHint')}</small>
          <button className="gold-primary" disabled={!canSave}>{mode === 'edit' ? t('profile.saveEdit') : t('profile.save')}</button>
        </form>
        <button className="gold-text" onClick={onRecover}>{t('recovery.title')}</button>
      </section>
    </div>
    {helpOpen && <UidHelp onClose={() => setHelpOpen(false)}/>}
  </>;
}

function UidHelp({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return <div className="gold-backdrop gold-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
    <section className="gold-dialog gold-uid-dialog" onClick={event => event.stopPropagation()}>
      <div className="gold-lightbox-head"><div><span className="gold-kicker">{t('uid.sample')}</span><h2>{t('profile.uidHelp')}</h2></div><button className="gold-lightbox-close" onClick={onClose}>{t('common.close')}</button></div>
      <p>{t('uid.instructions')}</p>
      <img src="/assets/app-guide/11-uid-location.png?v=redacted-20261103" alt={t('uid.imageAlt')}/>
      <AppEntryLink placement="guide_download">{t('uid.openApp')}</AppEntryLink>
    </section>
  </div>;
}

function RecordPage({ config, now, me, go, onEditProfile }: { config: GoldConfig; now: number; me: GoldMe; go: (page: GoldPage, runId?: string) => void; onEditProfile: () => void }) {
  const { t } = useTranslation();
  const latest = latestByRun(me);
  return <>
    <PageHeader eyebrow={t('record.eyebrow')} title={t('record.title')}/>
    <section className="gold-stats">
      <div><strong>{me.runs.length}</strong><span>{t('record.played')}</span><small>{t('record.perDay')}</small></div>
      <div><strong>{me.validDays.length}</strong><span>{t('record.listed')}</span><small>{t('record.approved')}</small></div>
      <div><strong>{me.totalGold}</strong><span>{t('record.total')}</span><small>g</small></div>
    </section>
    <section className="gold-card gold-final-ledger">
      {Array.from({ length: config.rules.activity_days }, (_, index) => {
        const dateKey = `D${index + 1}`;
        const valid = me.validDays.find(day => day.dateKey === dateKey);
        const run = me.runs.find(item => item.dateKey === dateKey);
        const submission = run ? latest.get(run.id) : undefined;
        const dayNumber = index + 1;
        const dayState = historyDayState(dayNumber, config.current);
        const access = evidenceAccess(run, submission, now);
        const status = valid
          ? t('record.validValue', { score: valid.goldAmount })
          : submission?.status === 'supplement_requested'
            ? t(access.canUpload ? 'record.supplement' : 'evidence.supplementExpired')
            : submission
              ? statusMessage(submission.status, t)
              : run
                ? run.phase === 'SETTLED'
                  ? access.canUpload ? t('home.claimReady') : t('evidence.initialExpired')
                  : t('home.playing')
                : t(dayState === 'past' ? 'record.notParticipated' : dayState === 'future' ? 'record.notOpen' : 'home.notStarted');
        return <article key={dateKey}>
          <b>{t('record.day', { day: index + 1 })}</b>
          {run?.phase === 'SETTLED' ? <button className="gold-text" onClick={() => go('evidence', run.id)}>{status}</button> : <span>{status}</span>}
        </article>;
      })}
    </section>
    {me.participant.registrationSequence && <button className="gold-secondary" onClick={onEditProfile}>{t('record.edit')}</button>}
    <button className="gold-secondary" onClick={() => go('evidence')}>{t('record.openEvidence')}</button>
  </>;
}

function EvidencePage({ config, now, selected, me, act, refresh, go, setNotice }: {
  config: GoldConfig;
  now: number;
  selected: GoldRunView | null;
  me: GoldMe;
  act: (work: () => Promise<void>) => Promise<void>;
  refresh: () => Promise<GoldMe>;
  go: (page: GoldPage, runId?: string) => void;
  setNotice: (value: string) => void;
}) {
  const { t } = useTranslation();
  const latest = latestByRun(me);
  const available = me.runs.filter(run => evidenceAccess(run, latest.get(run.id), now).canUpload || run.id === selected?.id).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const submission = selected ? latest.get(selected.id) : undefined;
  const access = evidenceAccess(selected, submission, now);

  const submit = () => {
    if (!selected || !files.length || !profileIsComplete(me.participant) || !evidenceAccess(selected, submission, activityNow(config, performance.now())).canUpload) return;
    void act(async () => {
      setSubmitting(true);
      const [freshConfig, freshMe] = await Promise.all([loadConfig(), api<GoldMe>('/api/gold-v2/me')]);
      if (!profileIsComplete(freshMe.participant)) throw new Error('incomplete_prize_profile');
      if (freshMe.participant.id !== me.participant.id || freshMe.participant.uid !== me.participant.uid) throw new Error('gold_run_scope');
      const assertUploadAllowed = () => {
        if (!evidenceAccess(selected, submission, activityNow(freshConfig, performance.now())).canUpload) throw new Error('submission_deadline_passed');
      };
      assertUploadAllowed();
      const fileIds: string[] = [];
      for (const file of files) {
        const base64 = await readBase64(file);
        assertUploadAllowed();
        const uploaded = await api<{ id: string }>('/api/gold-v2/uploads', {
          mime: file.type,
          base64,
          ...mutation(0),
        });
        fileIds.push(uploaded.id);
      }
      assertUploadAllowed();
      await api('/api/gold-v2/evidence/daily', {
        runId: selected.id,
        files: fileIds,
        ...mutation(submission?.version ?? 0),
      });
      setFiles([]);
      await refresh();
      setNotice(t('evidence.submittedToast'));
    }).finally(() => setSubmitting(false));
  };

  return <>
    <PageHeader eyebrow={t('evidence.eyebrow')} title={t('evidence.title')}/>
    <section className="gold-card">
      {!selected && <div className="gold-notice">{t('evidence.noRun')}</div>}
      {selected && <>
        <div className="gold-evidence-run"><span>{t('record.day', { day: Number(selected.dateKey.slice(1)) })} · {t('game.gold')}</span><b>{selected.goldAmount}g</b><small>{submission ? statusMessage(submission.status, t) : t('evidence.notSubmitted')}</small></div>
        {available.length > 1 && <label>{t('evidence.chooseTask')}<select value={selected.id} onChange={event => go('evidence', event.target.value)}>{available.map(run => <option key={run.id} value={run.id}>{t('record.day', { day: Number(run.dateKey.slice(1)) })} · {run.goldAmount}g</option>)}</select></label>}
        {access.deadline && <p className="gold-deadline">{t('evidence.deadline', { deadline: instantLabel(access.deadline, config.campaign.timezone) })}</p>}
      </>}
      {access.state === 'initial_expired' && <div className="gold-notice danger" role="status">{t('evidence.initialExpired')}</div>}
      {access.state === 'supplement_expired' && <div className="gold-notice danger" role="status">{t('evidence.supplementExpired')}</div>}
      <a className="gold-calendar-sample" data-testid="calendar-sample" href={guideAsset(CALENDAR_GUIDE_STEP)} target="_blank" rel="noreferrer">
        <img src={guideAsset(CALENDAR_GUIDE_STEP)} alt={t('evidence.sampleAlt')}/>
        <span><b>{t('evidence.sampleTitle')}</b><small>{t('evidence.sampleHint')}</small></span>
      </a>
      {submission?.status === 'approved' && <div className="gold-approved-state"><span>✓</span><div><h2>{t('result.approved', { score: selected?.goldAmount ?? 0, ending: t('result.tomorrow') })}</h2></div></div>}
      {submission?.status === 'rejected' && <div className="gold-notice danger">{t('status.rejected')}: {submission.reason}</div>}
      {submission?.status === 'supplement_requested' && <div className="gold-notice">{submission.reason}{submission.supplementDeadline ? ` · ${instantLabel(submission.supplementDeadline, config.campaign.timezone)}` : ''}</div>}
      {access.canUpload && <form className="gold-form" onSubmit={event => { event.preventDefault(); submit(); }}>
        <label className="gold-file-picker"><span>{t('evidence.selectFile')}</span><small>PNG / JPEG · max 5MB</small><input type="file" accept="image/png,image/jpeg" multiple onChange={event => setFiles(Array.from(event.currentTarget.files ?? []).slice(0, 10))} data-testid="gold-evidence-files"/></label>
        {files.length > 0 && <div className="gold-file-list">{files.map((file, index) => <span key={`${file.name}-${file.size}`}>{file.name}<small>{index === 0 ? `${t('evidence.primaryImage')} · ` : ''}{(file.size / 1024).toFixed(0)}KB</small></span>)}</div>}
        <button className="gold-primary" disabled={submitting || !files.length}>{submitting ? t('evidence.uploading') : submission?.status === 'supplement_requested' ? t('action.supplement') : t('evidence.submit')}</button>
        <button type="button" className="gold-text" onClick={() => go('guide')}>{t('action.viewGuide')}</button>
      </form>}
      {submission && <div className="gold-receipt" data-testid="gold-evidence-receipt">
        <b>{t('result.receipt')}</b><span>{t('evidence.recordId')}: {submission.id}</span><span>{t('evidence.reviewStatus')}: {statusMessage(submission.status, t)}</span><span>{t('evidence.firstSubmitted')}: {instantLabel(submission.firstSubmittedAt, config.campaign.timezone)}</span><span>{t('evidence.materialVersion')}: V{submission.submissionVersion}</span>
      </div>}
      {submission && submission.status !== 'supplement_requested' && <button className="gold-secondary" onClick={() => go('result')}>{t('evidence.returnStatus')}</button>}
    </section>
  </>;
}

function BoardPage({ me, rules }: { me: GoldMe; rules: () => void }) {
  const { t } = useTranslation();
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    void api<Board>('/api/gold-v2/board').then(setBoard).catch(cause => setError(messageFor(cause, t)));
  }, [t]);
  return <>
    <div className="gold-board-title"><PageHeader eyebrow={t('board.eyebrow')} title={t('board.title')}/><button className="gold-board-help" onClick={rules} aria-label={t('board.rulesAria')}><span>？</span><small>{t('board.help')}</small></button></div>
    <section className="gold-card">
      <div className="gold-board-summary">
        <p>{t('board.summary1')}</p>
        <p>{t('board.summary2')}</p>
      </div>
      {error && <div className="gold-notice danger">{error}</div>}
      {!board ? <p>{t('board.loading')}</p> : !board.rows.length ? <div className="gold-empty">{t('board.empty')}</div> : <div className="gold-board-list">
        {board.rows.map(row => <div key={`${row.tieStart}-${row.tieEnd}-${row.nickname}`} className={row.mine ? 'mine' : ''}>
          <strong>{row.tieStart === row.tieEnd ? row.rank : `${row.tieStart}-${row.tieEnd}`}</strong>
          <div><b>{row.nickname}</b><small>{t('board.validDays', { count: row.validDays })}</small></div>
          <span>{row.totalGold}<small>g</small></span>
        </div>)}
      </div>}
      <p className="gold-fineprint">{t('board.fineprint')}</p>
    </section>
  </>;
}

function guideAsset(step: (typeof GUIDE_STEPS)[number]): string {
  return `/assets/app-guide/${step.asset.split('/').at(-1)}`;
}

function GuidePage({ config, go }: { config: GoldConfig; go: (page: GoldPage, runId?: string) => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number | null>(null);
  return <>
    <PageHeader eyebrow={t('guide.eyebrow')} title={t('action.guide')}/>
    <section className="gold-card">
      <div className="gold-guide-actions"><AppEntryLink placement="guide_download">{t('uid.openApp')}</AppEntryLink><AppEntryLink className="gold-secondary" placement="guide_go_app">{t('action.goGold')}</AppEntryLink></div>
      <p className="gold-fineprint">{t('guide.intro')}</p>
    </section>
    <details className="gold-card gold-guide-faq"><summary>{t('guide.faqTitle')}</summary><p>{t('guide.faqBody', { note: t('guide.experienceNote') })}</p></details>
    <section className="gold-card gold-guide-list">
      {GUIDE_STEPS.map((step, index) => <button key={step.originalImage} type="button" data-testid="guide-step" data-image-number={step.originalImage} onClick={() => setExpanded(index)}>
        <span className="gold-guide-number">{index + 1}</span>
        <img src={guideAsset(step)} alt={t('guide.imageAlt', { title: t(step.titleKey) })} loading="lazy"/>
        <span className="gold-guide-copy"><b>{t(step.titleKey)}</b><small>{t(step.instructionKey)}</small><em>{t('guide.enlarge')}</em></span>
      </button>)}
    </section>
    <button className="gold-secondary" onClick={() => go('evidence')}>{t('home.recentShot')}</button>
    {expanded !== null && <div className="gold-backdrop gold-lightbox" data-testid="guide-lightbox" role="dialog" aria-modal="true" onClick={() => setExpanded(null)}>
      <section className="gold-dialog gold-lightbox-dialog" onClick={event => event.stopPropagation()}>
        <div className="gold-lightbox-head"><div><span className="gold-kicker">{t('guide.original', { number: GUIDE_STEPS[expanded]!.originalImage })}</span><h2>{t(GUIDE_STEPS[expanded]!.titleKey)}</h2></div><button className="gold-lightbox-close" onClick={() => setExpanded(null)}>{t('common.close')}</button></div>
        <img src={guideAsset(GUIDE_STEPS[expanded]!)} alt={t('guide.enlargedAlt', { title: t(GUIDE_STEPS[expanded]!.titleKey) })}/>
        <p>{t(GUIDE_STEPS[expanded]!.instructionKey)}</p>
        <div className="gold-lightbox-nav"><button className="gold-secondary" disabled={expanded === 0} onClick={() => setExpanded(value => Math.max(0, (value ?? 0) - 1))}>{t('common.previous')}</button><button className="gold-secondary" disabled={expanded === GUIDE_STEPS.length - 1} onClick={() => setExpanded(value => Math.min(GUIDE_STEPS.length - 1, (value ?? 0) + 1))}>{t('common.next')}</button></div>
      </section>
    </div>}
  </>;
}

function RecoveryPage({ act, refresh, go, setNotice }: {
  act: (work: () => Promise<void>) => Promise<void>;
  refresh: () => Promise<GoldMe>;
  go: (page: GoldPage, runId?: string) => void;
  setNotice: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(() => location.hash.slice(1));
  const recover = () => void act(async () => {
    let token = value.trim();
    if (/^https?:/i.test(token)) token = new URL(token).hash.slice(1);
    await api('/api/gold-v2/recover', { token });
    await refresh();
    setNotice(t('recovery.restored'));
    go('home');
  });
  return <><PageHeader eyebrow={t('recovery.eyebrow')} title={t('recovery.title')}/><section className="gold-card gold-form"><p>{t('recovery.body')}</p><label>{t('recovery.input')}<input value={value} onChange={event => setValue(event.target.value)} autoComplete="off"/></label><button className="gold-primary" onClick={recover}>{t('recovery.action')}</button></section></>;
}

function PageHeader({ eyebrow, title }: { eyebrow?: string; title: string }) {
  return <header className="gold-page-title">{eyebrow && <span>{eyebrow}</span>}<h1>{title}</h1></header>;
}

function SavePrompt({ onClose, onRecovery }: { onClose: () => void; onRecovery: () => void }) {
  const { t } = useTranslation();
  return <div className="gold-backdrop" role="dialog" aria-modal="true"><section className="gold-dialog">
    <span className="gold-dialog-icon">☆</span>
    <h2>{t('savep.title')}</h2>
    <p>{t('savep.instructions')}</p>
    <button className="gold-secondary" onClick={onRecovery}>{t('recovery.copy')}</button>
    <small className="gold-fineprint">{t('savep.recoveryHint')}</small>
    <button className="gold-primary" onClick={onClose}>{t('common.gotIt')}</button>
  </section></div>;
}

export function RulesDialog({ config, onClose }: { config: Pick<GoldConfig, 'campaign' | 'rules'>; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const locale = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeZone: config.campaign.timezone,
  }).format(new Date(`${value}T12:00:00Z`));
  const start = new Date(`${config.campaign.startDate}T12:00:00Z`);
  const dateForDay = (day: number) => new Date(start.getTime() + (day - 1) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const end = dateForDay(config.rules.activity_days);
  return <div className="gold-backdrop" role="dialog" aria-modal="true"><section className="gold-dialog gold-rules">
    <h2>{t('rules.title')}</h2>
    <p>{t('rules.short1')}</p><p>{t('rules.short2')}</p><p>{t('rules.short3')}</p><p>{t('rules.short4')}</p>
    <details open><summary><b>{t('rules.prizeTitle')}</b></summary>
      <p>{t('rules.prizeEligibility')}</p><p>{t('rules.prizeBody')}</p><p>{t('rules.prizeTies')}</p>
    </details>
    <details><summary><b>{t('rules.reviewTitle')}</b></summary>
      <p>{t('rules.reviewBody', {
        timezone: config.campaign.timezone,
        provisional: formatDate(dateForDay(config.rules.schedule.provisional_board_earliest_day)),
        final: formatDate(dateForDay(config.rules.schedule.final_board_earliest_day)),
        payout: t('rules.payoutThreeDays'),
      })}</p>
      <p>{t('rules.activityDates', { start: formatDate(config.campaign.startDate), end: formatDate(end), timezone: config.campaign.timezone })}</p>
    </details>
    <button className="gold-primary" onClick={onClose}>{t('rules.close')}</button>
  </section></div>;
}
